// src/stage2/Stage2.jsx
import React, { useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import MainCharacterPlayer from './characters/MainCharacterPlayer';
import FirstPersonControls from './FirstPersonControls';
import { Stage2Environment } from './Stage2Environment';
import { Stage2Characters } from './characters/Stage2Characters';
import S2LifeMeter from './S2LifeMeter';
import { BIKES } from './sceneExterior/BikesField';
import { ConversationProvider } from "./conversation/ConversationContext";
import ConversationHUD from "./conversation/ConversationHUD";

// Static sun (doesn't follow the camera)
function StaticSun() {
  const ref = React.useRef();
  React.useEffect(() => {
    if (!ref.current) return;
    ref.current.castShadow = true;

    // crisper shadows
    ref.current.shadow.mapSize.set(4069, 4069); //4069 4069 erases "shadow stripes" effect; but maybe use slightly textured grounds instead?
    // ref.current.shadow.mapSize.set(2048, 2048); //4069 4069 erases "shadow stripes" effect; but maybe use slightly textured grounds instead?
    ref.current.shadow.bias = -0.00015;
    ref.current.shadow.radius = 0.85;

    // shadow camera box
    const cam = ref.current.shadow.camera;
      Object.assign(cam, {
        left: -70,
        right: 70,
        top: 45,
        bottom: -45,
        near: 0.5,
        far: 200
      });
    cam.updateProjectionMatrix();
  }, []);
  return (
    <>
      <directionalLight
        ref={ref}
        position={[-30, 35, -30]}   // low angle, eastern horizon
        intensity={7}               // slightly softer than noon
        color="#ffffff"             // warm orange-gold hue
        castShadow
      />
      <ambientLight intensity={1} color="#d6cbb8" />
    </>
  );
}

// Simple gradient sky dome (early-morning feel)
function GradientSky() {
  const materialRef = React.useRef();

  const uniforms = React.useMemo(
    () => ({
      topColor:    { value: new THREE.Color('#2fa8f4') },  // deep blue
      bottomColor: { value: new THREE.Color('#fff09a') },  // soft orange/pink
    }),
    []
  );

  const vertexShader = `
    varying vec3 vWorldPosition;
    void main() {
      vec4 worldPosition = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPosition.xyz;
      gl_Position = projectionMatrix * viewMatrix * worldPosition;
    }
  `;

  const fragmentShader = `
    uniform vec3 topColor;
    uniform vec3 bottomColor;
    varying vec3 vWorldPosition;
    void main() {
      // map Y from [-1,1] to [0,1] for vertical blend
      float h = normalize(vWorldPosition).y * 0.5 + 0.5;
      vec3 color = mix(bottomColor, topColor, h);
      gl_FragColor = vec4(color, 1.0);
    }
  `;

  return (
    <mesh scale={200}>
      <sphereGeometry args={[1, 32, 32]} />
      <shaderMaterial
        ref={materialRef}
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        side={THREE.BackSide}
        depthWrite={false}
      />
    </mesh>
  );
}

const LS_TOP_KEY = "s2_rings_top";
const LS_LAST_KEY = "s2_rings_last";

function safeNumber(raw, fallback = 0) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export default function Stage2() {
  const [isWalking, setIsWalking] = React.useState(false);

  // (Optional) keep these if your other meters depend on them later
  // const [isSleepSwitchOn, setIsSleepSwitchOn] = React.useState(false);
  // const [arrowPosition, setArrowPosition] = React.useState(0);
  const [score, setScore] = React.useState(0);

  // Bikes
const [moveMode, setMoveMode] = React.useState("walk"); // walk | mount | ride
const [activeBikeId, setActiveBikeId] = React.useState(null);
const [nearBikeId, setNearBikeId] = React.useState(null);

const mode = activeBikeId ? "ride" : (nearBikeId ? "near" : "walk");

React.useEffect(() => {
  console.log("[MOVE MODE]", mode, { nearBikeId, activeBikeId });
}, [mode, nearBikeId, activeBikeId]);


const handleRideStart = React.useCallback((bikeId) => {
    console.log("[Stage2 handleRideStart]", bikeId, "moveMode:", moveMode);

  if (moveMode !== "walk") return;
  setActiveBikeId(bikeId);
  setMoveMode("mount");
  window.setTimeout(() => setMoveMode("ride"), 550); // mount animation window
}, [moveMode]);

const handleBikeNearChange = React.useCallback((bikeId, isNear) => {
  setNearBikeId((prev) => {
    if (isNear) return bikeId;
    // only clear if the bike leaving was the one we considered "near"
    return prev === bikeId ? null : prev;
  });
}, []);

const handleRideEnd = React.useCallback(() => {
  setMoveMode("walk");
  setActiveBikeId(null);
  setNearBikeId(null);
}, []);

// --------------------
// LIVE BIKE STORE (pos/yaw per bike, no rerender spam)
// --------------------
const bikesLiveRef = React.useRef(null);

if (!bikesLiveRef.current) {
  const init = {};
  for (const b of BIKES) {
    init[b.id] = {
      pos: new THREE.Vector3(...b.pos),
      yaw: 0,
      flying: false,
    };
  }
  bikesLiveRef.current = init;
}

// FPC reads CURRENT positions from this store
// Bike Position re-mapped from BikesField export BIKES -- this is for camera to lerp; moving user to "riding position" no matter which bike is picked
const bikePosById = React.useMemo(() => {
  const map = {};
  const store = bikesLiveRef.current;
  for (const id in store) map[id] = store[id].pos;
  return map;
}, []);

// FPC writes updated transform here every frame while riding
const handleRideUpdate = React.useCallback((id, { position, yaw, flying }) => {
  const store = bikesLiveRef.current;
  const b = store?.[id];
  if (!b) return;
  b.pos.copy(position);
  b.yaw = yaw;
  b.flying = !!flying;
}, []);

  ///////////////////// 
  // Rings
  const [ringCurrent, setRingCurrent] = React.useState(0);
  const [ringTop, setRingTop] = React.useState(() =>
    safeNumber(localStorage.getItem(LS_TOP_KEY), 0)
  );
  const [ringLast, setRingLast] = React.useState(() =>
    safeNumber(localStorage.getItem(LS_LAST_KEY), 0)
  );

  // Called by a ring when collected
  const handleRingCollect = React.useCallback((ringId) => {
    setRingCurrent((prev) => {
      const next = prev + 1;

      // Last = latest run score (live update)
      setRingLast(next);
      localStorage.setItem(LS_LAST_KEY, String(next));

      // Top = max score across runs
      setRingTop((t) => {
        const nextTop = next > t ? next : t;
        localStorage.setItem(LS_TOP_KEY, String(nextTop));
        return nextTop;
      });

      return next;
    });
  }, []);

  ///////////////////
  // NPC INTERACTION & Environment "switch" State
  const playerPosRef = useRef(new THREE.Vector3());
  const [envVariant, setEnvVariant] = useState('playground'); // 'playground' | 'classroom'

return (
    <ConversationProvider onPlayTick={() => setScore((s) => s + 1)}>

    <div className="page stage2">
      <S2LifeMeter
        score={score}
        // isSleepSwitchOn={isSleepSwitchOn}
        // setIsSleepSwitchOn={setIsSleepSwitchOn}
        // setArrowPosition={setArrowPosition}
        ringCurrent={ringCurrent}
        ringTop={ringTop}
        ringLast={ringLast}
      />
      
      <ConversationHUD />

      <div className="canvas-container">
        <Canvas
          shadows
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl }) => {
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.toneMapping = THREE.NoToneMapping;
            gl.toneMappingExposure = 1.0;
          }}
        >
          <GradientSky />
          <StaticSun />

          {/* Player shadow that reacts to movement */}
          <MainCharacterPlayer
            baseScale={1.3}
            shadowScaleXZ={[1.6, 1.7]}
            offsetForward={0}
            isWalking={isWalking}
            idleClipName="BreathingIdle"
            startWalkClip="StartWalking"
            walkLoopClip="WalkingInPlace"
            stopWalkClip="StopWalking"
          />

          {/* Camera - the MAIN VIEW - controls speed, bike, walk controls, et all camera movement */}
          <FirstPersonControls
            envVariant={envVariant}
            playerPosRef={playerPosRef}
            onWalkStateChange={setIsWalking}
            nearBikeId={nearBikeId}
            activeBikeId={activeBikeId}
            onRideStart={handleRideStart}
            onRideEnd={handleRideEnd}
            bikePosById={bikePosById}
            onRideUpdate={handleRideUpdate}
          />

          {/* via "Playground.jsx" - Holds all "props" (elements) in the sceneExterior (orsceneInterior) */}
          <Stage2Environment 
            variant={envVariant}
            setVariant={setEnvVariant}
            onRingCollect={handleRingCollect} 
            onRideStart={handleRideStart}
            onBikeNearChange={handleBikeNearChange}
            bikesLiveRef={bikesLiveRef}
          />

          {/* NPCs and Interactive Characters */}
          <Stage2Characters playerPosRef={playerPosRef} />

        </Canvas>
      </div>
    </div>
    </ConversationProvider>
  );
}
